import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthProvider';
import { getCloudSettings, saveCloudSettings } from './userProgress';

export type FontSize = 'compact' | 'comfortable' | 'large';

export interface Tweaks {
  fontSize: FontSize;
  showScript: boolean;
  darkMode: boolean;
}

const STORAGE_KEY = 'jlpt:tweaks:v1';
const DEFAULTS: Tweaks = {
  fontSize: 'comfortable',
  showScript: false,
  darkMode: false,
};

// Single brand identity (indigo). Theme picker removed in Phase 0 — old teal/amber values in
// stored tweaks are simply ignored on load (see sanitizeStored / sanitizeCloud).
const BRAND = { brand: '#4338CA', brandDark: '#3730A3', brandLight: '#EEF2FF', brandMid: '#818CF8' };

const FS_MAP: Record<FontSize, string> = {
  compact: '14px',
  comfortable: '15px',
  large: '17px',
};

interface TweaksContextValue {
  tweaks: Tweaks;
  setTweak: <K extends keyof Tweaks>(key: K, value: Tweaks[K]) => void;
}

const TweaksContext = createContext<TweaksContextValue | null>(null);

/** Keep only recognized keys — silently drops legacy fields (e.g. `theme`). */
function sanitizeStored(raw: unknown): Partial<Tweaks> {
  if (typeof raw !== 'object' || raw === null) return {};
  const r = raw as Record<string, unknown>;
  const out: Partial<Tweaks> = {};
  if (r.fontSize === 'compact' || r.fontSize === 'comfortable' || r.fontSize === 'large')
    out.fontSize = r.fontSize;
  if (typeof r.darkMode === 'boolean') out.darkMode = r.darkMode;
  if (typeof r.showScript === 'boolean') out.showScript = r.showScript;
  return out;
}

function loadTweaks(): Tweaks {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...sanitizeStored(JSON.parse(raw)) };
  } catch {
    return DEFAULTS;
  }
}

function applyTweaks(tweaks: Tweaks) {
  const r = document.documentElement;
  r.style.setProperty('--brand', BRAND.brand);
  r.style.setProperty('--brand-dark', BRAND.brandDark);
  r.style.setProperty('--brand-light', BRAND.brandLight);
  r.style.setProperty('--brand-mid', BRAND.brandMid);
  r.style.setProperty('--player-accent', BRAND.brandMid);

  if (tweaks.darkMode) {
    r.style.setProperty('--bg', '#0A0912');
    r.style.setProperty('--surface', '#16132A');
    r.style.setProperty('--surface-2', '#201C38');
    r.style.setProperty('--border', 'rgba(255,255,255,0.08)');
    r.style.setProperty('--text-1', '#F0EDFF');
    r.style.setProperty('--text-2', '#9F9BBF');
    r.style.setProperty('--text-3', '#5C5878');
  } else {
    r.style.setProperty('--bg', '#F0EFF8');
    r.style.setProperty('--surface', '#FFFFFF');
    r.style.setProperty('--surface-2', '#E8E7F3');
    r.style.setProperty('--border', 'rgba(67,56,202,0.11)');
    r.style.setProperty('--text-1', '#1E1A3C');
    r.style.setProperty('--text-2', '#6B6487');
    r.style.setProperty('--text-3', '#A89FC4');
  }
  r.style.fontSize = FS_MAP[tweaks.fontSize];
}

function sanitizeCloud(raw: Record<string, unknown>): Partial<Tweaks> {
  const out: Partial<Tweaks> = {};
  if (raw.fontSize === 'compact' || raw.fontSize === 'comfortable' || raw.fontSize === 'large')
    out.fontSize = raw.fontSize;
  if (typeof raw.darkMode === 'boolean') out.darkMode = raw.darkMode;
  if (typeof raw.showScript === 'boolean') out.showScript = raw.showScript;
  return out;
}

export function TweaksProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const [tweaks, setTweaks] = useState<Tweaks>(loadTweaks);
  const hydratedUidRef = useRef<string | null>(null);
  const cloudSyncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastWrittenRef = useRef<string>(JSON.stringify(tweaks));

  // Apply tweaks to CSS + cache locally on every change.
  useEffect(() => {
    applyTweaks(tweaks);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tweaks));
    } catch {
      // ignore
    }
  }, [tweaks]);

  // Hydrate from cloud when the user signs in for the first time this session.
  useEffect(() => {
    if (!uid) {
      hydratedUidRef.current = null;
      return;
    }
    if (hydratedUidRef.current === uid) return;
    hydratedUidRef.current = uid;
    let cancelled = false;
    getCloudSettings(uid)
      .then((raw) => {
        if (cancelled || !raw) return;
        const patch = sanitizeCloud(raw);
        if (Object.keys(patch).length === 0) return;
        setTweaks((prev) => {
          const next = { ...prev, ...patch };
          lastWrittenRef.current = JSON.stringify(next);
          return next;
        });
      })
      .catch(() => {
        // network failure: keep local
      });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  // Debounced push to cloud when signed in.
  useEffect(() => {
    if (!uid) return;
    const serialized = JSON.stringify(tweaks);
    if (serialized === lastWrittenRef.current) return;
    if (cloudSyncTimer.current) clearTimeout(cloudSyncTimer.current);
    cloudSyncTimer.current = setTimeout(() => {
      lastWrittenRef.current = serialized;
      saveCloudSettings(uid, tweaks as unknown as Record<string, unknown>).catch(() => {
        // ignore; will retry on next change
      });
    }, 600);
    return () => {
      if (cloudSyncTimer.current) clearTimeout(cloudSyncTimer.current);
    };
  }, [uid, tweaks]);

  const setTweak = <K extends keyof Tweaks>(key: K, value: Tweaks[K]) => {
    setTweaks((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <TweaksContext.Provider value={{ tweaks, setTweak }}>
      {children}
    </TweaksContext.Provider>
  );
}

export function useTweaks() {
  const ctx = useContext(TweaksContext);
  if (!ctx) throw new Error('useTweaks must be inside <TweaksProvider>');
  return ctx;
}
